import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { ImageUploader } from '../../components/ImageUploader';
import { api, type Paginated, type ApiSuccess } from '../../lib/api';

interface AuctionRow {
  id: string;
  title: string;
  city: string | null;
  openingPrice: number | string;
  currentBid: number | string;
  startAt: string;
  endAt: string;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED' | 'COMPLETED';
  totalBids: number;
}

const STATUS_LABELS: Record<string, { label: string; color: 'green' | 'amber' | 'navy' | 'red' | 'gray' }> = {
  LIVE:      { label: 'مباشر',  color: 'red' },
  SCHEDULED: { label: 'قادم',   color: 'navy' },
  ENDED:     { label: 'منتهي',  color: 'gray' },
  COMPLETED: { label: 'مكتمل',  color: 'green' },
  CANCELLED: { label: 'ملغي',   color: 'red' },
};

export function AdminAuctionsPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'auctions', statusFilter],
    queryFn: () => api.get<Paginated<AuctionRow>>('/api/auctions', {
      query: { status: statusFilter, limit: 50 },
    }),
  });

  const list = data?.data ?? [];
  const counts = {
    total: data?.pagination?.total ?? 0,
    live: list.filter((a) => a.status === 'LIVE').length,
    scheduled: list.filter((a) => a.status === 'SCHEDULED').length,
    ended: list.filter((a) => a.status === 'ENDED').length,
    cancelled: list.filter((a) => a.status === 'CANCELLED').length,
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AuctionRow> }) => api.put(`/api/auctions/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'auctions'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/auctions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'auctions'] }),
  });

  return (
    <div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark">🔨 إدارة المزادات</h1>
          <p className="text-sm text-ink-muted mt-1">جدولة + إدارة + متابعة جميع مزادات المنصة</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>➕ مزاد جديد</Button>
      </div>

      {/* 📊 شارات الحالات */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
        <AuctionStat label="الإجمالي" value={counts.total}     color="navy"  icon="🔨"
          active={!statusFilter} onClick={() => setStatusFilter('')} />
        <AuctionStat label="مباشر"   value={counts.live}      color="red"   icon="🔴"
          active={statusFilter === 'LIVE'} onClick={() => setStatusFilter(statusFilter === 'LIVE' ? '' : 'LIVE')} pulse />
        <AuctionStat label="قادم"     value={counts.scheduled} color="navy"  icon="📅"
          active={statusFilter === 'SCHEDULED'} onClick={() => setStatusFilter(statusFilter === 'SCHEDULED' ? '' : 'SCHEDULED')} />
        <AuctionStat label="منتهي"    value={counts.ended}     color="gray"  icon="⏹"
          active={statusFilter === 'ENDED'} onClick={() => setStatusFilter(statusFilter === 'ENDED' ? '' : 'ENDED')} />
        <AuctionStat label="ملغي"     value={counts.cancelled} color="red"   icon="✕"
          active={statusFilter === 'CANCELLED'} onClick={() => setStatusFilter(statusFilter === 'CANCELLED' ? '' : 'CANCELLED')} />
      </div>

      {showCreate && (
        <CreateAuctionModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['admin', 'auctions'] });
          }}
        />
      )}
      {editingId && (
        <EditAuctionModal
          id={editingId}
          onClose={() => setEditingId(null)}
          onSuccess={() => {
            setEditingId(null);
            qc.invalidateQueries({ queryKey: ['admin', 'auctions'] });
          }}
        />
      )}

      <Card>
        <CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>العنوان</Th>
                <Th>المدينة</Th>
                <Th>سعر الافتتاح</Th>
                <Th>المزايدة الحالية</Th>
                <Th>المزايدات</Th>
                <Th>البداية</Th>
                <Th>الحالة</Th>
                <Th>إجراءات</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
              ) : !data?.data?.length ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">لا توجد مزادات</td></tr>
              ) : data.data.map((a) => (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <Td><div className="font-bold text-ink line-clamp-1">{a.title}</div></Td>
                  <Td>{a.city ?? '—'}</Td>
                  <Td className="font-bold">{Number(a.openingPrice).toLocaleString('en-US')}</Td>
                  <Td className="font-bold">{Number(a.currentBid).toLocaleString('en-US')}</Td>
                  <Td>{a.totalBids}</Td>
                  <Td className="text-xs">{new Date(a.startAt).toLocaleString('ar-SA')}</Td>
                  <Td><Badge color={STATUS_LABELS[a.status]?.color ?? 'gray'}>{STATUS_LABELS[a.status]?.label ?? a.status}</Badge></Td>
                  <Td>
                    <div className="flex gap-1 flex-wrap">
                      {/* ▶️ ابدأ مباشرة (لو SCHEDULED) */}
                      {a.status === 'SCHEDULED' && (
                        <Button size="sm" variant="primary" onClick={() => updateMutation.mutate({ id: a.id, data: { status: 'LIVE' as never } })}
                                title="ابدأ المزاد الآن">▶</Button>
                      )}
                      {/* ⏸ أوقف لو LIVE */}
                      {a.status === 'LIVE' && (
                        <Button size="sm" variant="primary" onClick={() => updateMutation.mutate({ id: a.id, data: { status: 'SCHEDULED' as never } })}
                                title="أعد جدولة المزاد">⏸</Button>
                      )}
                      {/* ■ أنهِ لو LIVE */}
                      {a.status === 'LIVE' && (
                        <Button size="sm" variant="danger" onClick={() => updateMutation.mutate({ id: a.id, data: { status: 'ENDED' as never } })}
                                title="أنهِ المزاد">■</Button>
                      )}
                      {/* 🚫 إلغاء (لو SCHEDULED أو LIVE) */}
                      {(a.status === 'SCHEDULED' || a.status === 'LIVE') && (
                        <Button size="sm" variant="ghost" onClick={() => {
                          if (confirm(`إلغاء المزاد "${a.title}"؟ لا يمكن استرجاعه.`))
                            updateMutation.mutate({ id: a.id, data: { status: 'CANCELLED' as never } });
                        }} title="إلغاء">🚫</Button>
                      )}
                      {/* ✏️ تعديل */}
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(a.id)} title="تعديل">✏️</Button>
                      {/* 👁️ افتح الغرفة */}
                      <a href={`/auctions/${a.id}`} target="_blank" rel="noopener">
                        <Button size="sm" variant="ghost" title="افتح غرفة المزاد">👁️</Button>
                      </a>
                      {/* 🗑️ حذف نهائي */}
                      <Button size="sm" variant="ghost" onClick={() => { if (confirm(`حذف "${a.title}" نهائياً؟`)) deleteMutation.mutate(a.id); }}
                              title="حذف نهائي">🗑️</Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 📝 نموذج إنشاء مزاد كامل (مع كل الإعدادات المتقدمة)
// ═══════════════════════════════════════════════════════════════
interface CreateAuctionForm {
  // أساسيات
  title: string;
  titleEn: string;
  description: string;
  city: string;
  location: string;
  area: string;
  // التسعير
  openingPrice: string;
  bidIncrement: string;
  depositAmount: string;
  commissionRate: string;
  // التواريخ
  startAt: string;
  endAt: string;
  // وسائط
  coverImage: string;
  images: string;       // مفصولة بفواصل
  liveStreamUrl: string;
  // إعدادات
  settings: {
    allowGuests:         boolean;
    membersOnlyBidding:  boolean;
    requireRegistration: boolean;
    requireDeposit:      boolean;
    minVerificationTier: 'NONE' | 'GREEN' | 'GOLD' | 'DIAMOND';
    voiceEnabled:        boolean;
    chatEnabled:         boolean;
    allowSpeakRequests:  boolean;
    showAttendees:       boolean;
    showBidderNames:     boolean;
    antiSnipingEnabled:  boolean;
    antiSnipingSeconds:  string;
    maxBidCap:           string;
    showLiveStream:      boolean;
    recordingAllowed:    boolean;
    notifyOnStart:       boolean;
  };
}

const DEFAULT_FORM: CreateAuctionForm = {
  title: '', titleEn: '', description: '', city: '', location: '', area: '',
  openingPrice: '', bidIncrement: '50000', depositAmount: '50000', commissionRate: '2.5',
  startAt: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
  endAt:   new Date(Date.now() + 25 * 60 * 60 * 1000).toISOString().slice(0, 16),
  coverImage: '', images: '', liveStreamUrl: '',
  settings: {
    allowGuests: true,         membersOnlyBidding: true,
    requireRegistration: false, requireDeposit: false,
    minVerificationTier: 'NONE',
    voiceEnabled: true,        chatEnabled: true,
    allowSpeakRequests: true,  showAttendees: true,
    showBidderNames: true,
    antiSnipingEnabled: true,  antiSnipingSeconds: '60',
    maxBidCap: '',
    showLiveStream: true,      recordingAllowed: false,
    notifyOnStart: true,
  },
};

function CreateAuctionModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  return <AuctionFormModal mode="create" onClose={onClose} onSuccess={onSuccess} />;
}

interface AuctionDetail {
  id: string;
  title: string;
  titleEn: string | null;
  description: string | null;
  city: string | null;
  location: string | null;
  area: number | null;
  openingPrice: number | string;
  bidIncrement: number | string;
  depositAmount: number | string;
  commissionRate: number | string;
  startAt: string;
  endAt: string;
  coverImage: string | null;
  images: string[] | null;
  liveStreamUrl: string | null;
  settings: Record<string, unknown> | null;
}

function EditAuctionModal({ id, onClose, onSuccess }: { id: string; onClose: () => void; onSuccess: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['auction', id],
    queryFn: () => api.get<ApiSuccess<AuctionDetail>>(`/api/auctions/${id}`),
  });
  if (isLoading) return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl p-8 text-gray-500">⟳ جاري التحميل…</div>
    </div>
  );
  if (!data?.data) return null;
  return <AuctionFormModal mode="edit" auction={data.data} onClose={onClose} onSuccess={onSuccess} />;
}

interface AuctionFormModalProps {
  mode: 'create' | 'edit';
  auction?: AuctionDetail;
  onClose: () => void;
  onSuccess: () => void;
}

function AuctionFormModal({ mode, auction, onClose, onSuccess }: AuctionFormModalProps) {
  const initial = auction ? auctionToForm(auction) : DEFAULT_FORM;
  const [form, setForm] = useState<CreateAuctionForm>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (auction) setForm(auctionToForm(auction));
  }, [auction]);

  const set = <K extends keyof CreateAuctionForm>(key: K, value: CreateAuctionForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const setSetting = <K extends keyof CreateAuctionForm['settings']>(key: K, value: CreateAuctionForm['settings'][K]) =>
    setForm((f) => ({ ...f, settings: { ...f.settings, [key]: value } }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        title: form.title,
        titleEn: form.titleEn || undefined,
        description: form.description || undefined,
        city: form.city || undefined,
        location: form.location || undefined,
        area: form.area ? Number(form.area) : undefined,
        openingPrice: Number(form.openingPrice),
        bidIncrement: Number(form.bidIncrement),
        depositAmount: Number(form.depositAmount),
        commissionRate: Number(form.commissionRate),
        startAt: form.startAt,
        endAt: form.endAt,
        coverImage: form.coverImage || undefined,
        images: form.images ? form.images.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        liveStreamUrl: form.liveStreamUrl || undefined,
        settings: {
          ...form.settings,
          antiSnipingSeconds: Number(form.settings.antiSnipingSeconds),
          maxBidCap: form.settings.maxBidCap ? Number(form.settings.maxBidCap) : undefined,
        },
      };
      if (mode === 'edit' && auction) {
        await api.put(`/api/auctions/${auction.id}`, payload);
      } else {
        await api.post('/api/auctions', payload);
      }
      onSuccess();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="font-black text-xl text-navy-dark">{mode === 'edit' ? '✏️ تعديل المزاد' : '🔨 مزاد جديد'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">

          {/* ━━━━━━━━━━━━━━━━━━━━ أساسيات ━━━━━━━━━━━━━━━━━━━━ */}
          <Section title="📋 المعلومات الأساسية">
            <Input label="العنوان (عربي) *"  value={form.title}   onChange={(e) => set('title', e.target.value)} required />
            <Input label="Title (English)"     value={form.titleEn} onChange={(e) => set('titleEn', e.target.value)} />
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-ink mb-1">الوصف</label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
              />
            </div>
            <Input label="المدينة"  value={form.city}     onChange={(e) => set('city', e.target.value)} />
            <Input label="الموقع التفصيلي" value={form.location} onChange={(e) => set('location', e.target.value)} />
            <Input label="المساحة (م²)" type="number" value={form.area} onChange={(e) => set('area', e.target.value)} />
          </Section>

          {/* ━━━━━━━━━━━━━━━━━━━━ التسعير ━━━━━━━━━━━━━━━━━━━━ */}
          <Section title="💰 التسعير">
            <Input label="سعر الافتتاح (ر.س) *"  type="number" value={form.openingPrice}   onChange={(e) => set('openingPrice', e.target.value)} required />
            <Input label="مقدار الزيادة (ر.س)"   type="number" value={form.bidIncrement}   onChange={(e) => set('bidIncrement', e.target.value)} />
            <Input label="مبلغ التأمين (ر.س)"    type="number" value={form.depositAmount}  onChange={(e) => set('depositAmount', e.target.value)} />
            <Input label="نسبة السعي (%)"         type="number" step="0.1" value={form.commissionRate} onChange={(e) => set('commissionRate', e.target.value)} />
          </Section>

          {/* ━━━━━━━━━━━━━━━━━━━━ التواريخ ━━━━━━━━━━━━━━━━━━━━ */}
          <Section title="🕐 التواريخ">
            <Input label="تاريخ البداية *" type="datetime-local" value={form.startAt} onChange={(e) => set('startAt', e.target.value)} required />
            <Input label="تاريخ النهاية *" type="datetime-local" value={form.endAt}   onChange={(e) => set('endAt',   e.target.value)} required />
          </Section>

          {/* ━━━━━━━━━━━━━━━━━━━━ الوسائط ━━━━━━━━━━━━━━━━━━━━ */}
          <Section title="🖼️ الوسائط">
            <div className="md:col-span-2">
              <ImageUploader
                value={form.coverImage}
                onChange={(url) => set('coverImage', url ?? '')}
                purpose="AUCTION_COVER"
                label="🖼️ الصورة الرئيسية للمزاد"
                hint="عريضة (16:9) — حد أقصى 5MB"
                shape="wide"
              />
            </div>
            <Input label="رابط البث المباشر"    value={form.liveStreamUrl} onChange={(e) => set('liveStreamUrl', e.target.value)} />
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-ink mb-1">صور إضافية (روابط مفصولة بفاصلة)</label>
              <input
                value={form.images}
                onChange={(e) => set('images', e.target.value)}
                placeholder="https://… , https://… , https://…"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
              />
            </div>
          </Section>

          {/* ━━━━━━━━━━━━━━━━━━━━ التحكم بالوصول ━━━━━━━━━━━━━━━━━━━━ */}
          <Section title="🛡️ التحكم بالوصول">
            <Toggle label="السماح للزوار (غير المسجلين) بالمشاهدة"  on={form.settings.allowGuests}         onToggle={(v) => setSetting('allowGuests', v)} />
            <Toggle label="المزايدة للأعضاء فقط"                     on={form.settings.membersOnlyBidding}  onToggle={(v) => setSetting('membersOnlyBidding', v)} />
            <Toggle label="يتطلب تسجيل مسبق قبل الدخول"             on={form.settings.requireRegistration} onToggle={(v) => setSetting('requireRegistration', v)} />
            <Toggle label="يتطلب دفع عربون للمزايدة"                 on={form.settings.requireDeposit}      onToggle={(v) => setSetting('requireDeposit', v)} />
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-ink mb-1">الحد الأدنى للتوثيق المطلوب</label>
              <select
                value={form.settings.minVerificationTier}
                onChange={(e) => setSetting('minVerificationTier', e.target.value as never)}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
              >
                <option value="NONE">بدون شرط (الكل مسموح)</option>
                <option value="GREEN">أخضر فأكثر</option>
                <option value="GOLD">ذهبي فأكثر</option>
                <option value="DIAMOND">ماسي فقط</option>
              </select>
            </div>
          </Section>

          {/* ━━━━━━━━━━━━━━━━━━━━ التواصل ━━━━━━━━━━━━━━━━━━━━ */}
          <Section title="📢 التواصل">
            <Toggle label="تفعيل البث الصوتي للمحرّج" on={form.settings.voiceEnabled}       onToggle={(v) => setSetting('voiceEnabled', v)} />
            <Toggle label="تفعيل الدردشة (الشات)"     on={form.settings.chatEnabled}        onToggle={(v) => setSetting('chatEnabled', v)} />
            <Toggle label="السماح بطلب التحدّث"       on={form.settings.allowSpeakRequests} onToggle={(v) => setSetting('allowSpeakRequests', v)} />
            <Toggle label="عرض قائمة الحاضرين"        on={form.settings.showAttendees}      onToggle={(v) => setSetting('showAttendees', v)} />
          </Section>

          {/* ━━━━━━━━━━━━━━━━━━━━ المزايدة ━━━━━━━━━━━━━━━━━━━━ */}
          <Section title="🔨 المزايدة">
            <Toggle label="عرض أسماء المزايدين (لا = مزايدات مجهولة)" on={form.settings.showBidderNames}    onToggle={(v) => setSetting('showBidderNames', v)} />
            <Toggle label="تمديد المزاد عند مزايدة في آخر دقيقة"      on={form.settings.antiSnipingEnabled} onToggle={(v) => setSetting('antiSnipingEnabled', v)} />
            <Input label="مدة التمديد (بالثواني)" type="number" value={form.settings.antiSnipingSeconds} onChange={(e) => setSetting('antiSnipingSeconds', e.target.value)} />
            <Input label="سقف أعلى للمزايدة (ر.س - اختياري)" type="number" value={form.settings.maxBidCap} onChange={(e) => setSetting('maxBidCap', e.target.value)} hint="اتركه فارغاً = بدون سقف" />
          </Section>

          {/* ━━━━━━━━━━━━━━━━━━━━ خيارات أخرى ━━━━━━━━━━━━━━━━━━━━ */}
          <Section title="⚙️ خيارات أخرى">
            <Toggle label="عرض بث الفيديو"                       on={form.settings.showLiveStream}   onToggle={(v) => setSetting('showLiveStream', v)} />
            <Toggle label="السماح بتسجيل المزاد"                 on={form.settings.recordingAllowed} onToggle={(v) => setSetting('recordingAllowed', v)} />
            <Toggle label="إرسال إشعار للمشتركين عند بدء المزاد" on={form.settings.notifyOnStart}    onToggle={(v) => setSetting('notifyOnStart', v)} />
          </Section>

          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{err}</div>}

          <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 sticky bottom-0 bg-white pb-1">
            <Button type="button" variant="ghost" onClick={onClose}>إلغاء</Button>
            <Button type="submit" variant="gold" loading={busy}>
              {mode === 'edit' ? '💾 حفظ التعديلات' : '💾 حفظ وجدولة المزاد'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** يحوّل بيانات المزاد المحمّلة من API لشكل النموذج */
function auctionToForm(a: AuctionDetail): CreateAuctionForm {
  const s = (a.settings ?? {}) as Partial<CreateAuctionForm['settings']> & { maxBidCap?: number; antiSnipingSeconds?: number };
  return {
    title: a.title,
    titleEn: a.titleEn ?? '',
    description: a.description ?? '',
    city: a.city ?? '',
    location: a.location ?? '',
    area: a.area ? String(a.area) : '',
    openingPrice:   String(a.openingPrice ?? ''),
    bidIncrement:   String(a.bidIncrement ?? '50000'),
    depositAmount:  String(a.depositAmount ?? '50000'),
    commissionRate: String(a.commissionRate ?? '2.5'),
    startAt: new Date(a.startAt).toISOString().slice(0, 16),
    endAt:   new Date(a.endAt).toISOString().slice(0, 16),
    coverImage: a.coverImage ?? '',
    images: Array.isArray(a.images) ? a.images.join(', ') : '',
    liveStreamUrl: a.liveStreamUrl ?? '',
    settings: {
      allowGuests:         s.allowGuests         ?? true,
      membersOnlyBidding:  s.membersOnlyBidding  ?? true,
      requireRegistration: s.requireRegistration ?? false,
      requireDeposit:      s.requireDeposit      ?? false,
      minVerificationTier: (s.minVerificationTier as never) ?? 'NONE',
      voiceEnabled:        s.voiceEnabled        ?? true,
      chatEnabled:         s.chatEnabled         ?? true,
      allowSpeakRequests:  s.allowSpeakRequests  ?? true,
      showAttendees:       s.showAttendees       ?? true,
      showBidderNames:     s.showBidderNames     ?? true,
      antiSnipingEnabled:  s.antiSnipingEnabled  ?? true,
      antiSnipingSeconds:  String(s.antiSnipingSeconds ?? 60),
      maxBidCap:           s.maxBidCap ? String(s.maxBidCap) : '',
      showLiveStream:      s.showLiveStream      ?? true,
      recordingAllowed:    s.recordingAllowed    ?? false,
      notifyOnStart:       s.notifyOnStart       ?? true,
    },
  };
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border border-gray-200 rounded-xl p-4">
      <legend className="px-2 font-black text-navy-dark text-sm">{title}</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </fieldset>
  );
}

function Toggle({ label, on, onToggle }: { label: string; on: boolean; onToggle: (v: boolean) => void }) {
  return (
    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${on ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onToggle(e.target.checked)}
        className="w-5 h-5 accent-green-600"
      />
      <span className="text-sm font-bold text-ink flex-1">{label}</span>
    </label>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}

function AuctionStat({ label, value, color, icon, active, onClick, pulse }: {
  label: string; value: number; color: 'navy' | 'amber' | 'green' | 'red' | 'gray';
  icon: string; active?: boolean; onClick: () => void; pulse?: boolean;
}) {
  const colors = {
    navy:  { active: 'border-navy bg-navy-soft',     text: 'text-navy-dark' },
    amber: { active: 'border-amber-400 bg-amber-50', text: 'text-amber-700' },
    green: { active: 'border-green-400 bg-green-50', text: 'text-green-700' },
    red:   { active: 'border-red-400 bg-red-50',     text: 'text-red-700' },
    gray:  { active: 'border-gray-400 bg-gray-100',  text: 'text-gray-700' },
  }[color];
  return (
    <button
      onClick={onClick}
      className={`text-right p-3 rounded-2xl border-2 bg-white transition hover:shadow-brand ${
        active ? colors.active : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xl">{icon}</span>
        {pulse && value > 0 && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
      </div>
      <div className={`text-2xl font-black mt-1 ${colors.text}`}>{value}</div>
      <div className="text-[11px] text-ink-muted">{label}</div>
    </button>
  );
}
